import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { ChevronLeft, Play, Pause, Settings, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const SPEEDS = [
  { id: "slow", label: "Lent", duration: 5.0 },
  { id: "medium", label: "Moyen", duration: 3.0 },
  { id: "fast", label: "Rapide", duration: 1.8 }
];

const DURATIONS = [
  { id: 60, label: "1 min" },
  { id: 120, label: "2 min" },
  { id: 300, label: "5 min" }
];

export default function EmdrPage() {
  const [view, setView] = useState("intro");
  const [speed, setSpeed] = useState("medium");
  const [duration, setDuration] = useState(120);
  const [running, setRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [showSettings, setShowSettings] = useState(false);
  const timerRef = useRef(null);

  const currentSpeed = SPEEDS.find(s => s.id === speed);

  const startSession = useCallback(() => {
    setTimeLeft(duration);
    setRunning(true);
  }, [duration]);

  const stopSession = useCallback(() => {
    setRunning(false);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const togglePause = () => {
    setRunning(prev => !prev);
  };

  useEffect(() => {
    if (running && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setRunning(false);
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [running, timeLeft]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {view === "intro" && <Header />}
      <AnimatePresence mode="wait">
        {view === "intro" && (
          <motion.main key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <Link to="/dashboard" className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200">
              <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Retour
            </Link>

            <div className="mb-8">
              <p className="text-xs uppercase tracking-wider text-[#D9A892] font-medium mb-2" style={{ fontFamily: 'Manrope' }}>Urgence émotionnelle</p>
              <h1 className="text-3xl font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }} data-testid="emdr-title">SOS mouvements oculaires</h1>
              <p className="text-[#6A7771] text-sm">Stimulation bilatérale visuelle pour calmer une surcharge émotionnelle.</p>
            </div>

            <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 sm:p-8 mb-6" style={{ fontFamily: 'Manrope' }}>
              <p className="text-[#2C3531] leading-relaxed mb-4">
                Quand une émotion vous submerge, vos yeux peuvent vous aider à redescendre.
              </p>
              <p className="text-[#2C3531] leading-relaxed mb-4">
                Le principe est simple : suivez du regard le point lumineux qui se déplace de gauche à droite sur l'écran. Ce mouvement oculaire alterné aide votre cerveau à traiter l'émotion différemment.
              </p>
              <p className="text-[#2C3531] leading-relaxed mb-4">
                Vous n'avez rien à faire d'autre que suivre le point. Pas besoin de réfléchir, pas besoin de comprendre. Juste suivre des yeux.
              </p>
              <p className="text-[#5C6A61] font-medium">
                Installez-vous confortablement, le téléphone ou l'écran à hauteur des yeux, à environ 30-40 cm de votre visage.
              </p>
            </div>

            {/* Settings */}
            <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 mb-6">
              <h3 className="text-sm font-semibold text-[#2C3531] mb-4" style={{ fontFamily: 'Outfit' }}>Réglages</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-[#6A7771] mb-2 font-medium">Vitesse</p>
                  <div className="flex gap-2">
                    {SPEEDS.map(s => (
                      <button key={s.id} onClick={() => setSpeed(s.id)}
                        className={`flex-1 py-2.5 rounded-2xl text-sm font-medium transition-all duration-200 ${speed === s.id ? 'bg-[#6B8576] text-white' : 'bg-[#FAF9F6] border border-[#EBE7E0] text-[#2C3531] hover:border-[#6B8576]/30'}`}
                        data-testid={`emdr-speed-${s.id}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-[#6A7771] mb-2 font-medium">Durée</p>
                  <div className="flex gap-2">
                    {DURATIONS.map(d => (
                      <button key={d.id} onClick={() => { setDuration(d.id); setTimeLeft(d.id); }}
                        className={`flex-1 py-2.5 rounded-2xl text-sm font-medium transition-all duration-200 ${duration === d.id ? 'bg-[#6B8576] text-white' : 'bg-[#FAF9F6] border border-[#EBE7E0] text-[#2C3531] hover:border-[#6B8576]/30'}`}
                        data-testid={`emdr-duration-${d.id}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <button onClick={() => { setView("session"); startSession(); }} className="w-full py-4 bg-[#6B8576] text-white rounded-full font-medium hover:bg-[#546A5E] transition-all duration-300 text-sm flex items-center justify-center gap-2" data-testid="emdr-start">
              <Play className="w-4 h-4" strokeWidth={2} /> Commencer la séance
            </button>

            {/* Warning */}
            <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="emdr-safety">
              <h4 className="text-sm font-semibold text-amber-800 mb-2">Attention</h4>
              <p className="text-sm text-amber-700 leading-relaxed mb-2">
                Cet outil propose une stimulation bilatérale visuelle simplifiée à des fins d'auto-régulation émotionnelle. Il ne remplace en aucun cas une séance d'EMDR encadrée par un professionnel formé.
              </p>
              <p className="text-sm text-amber-700 leading-relaxed">
                Déconseillé en cas d'épilepsie ou de troubles visuels sensibles aux stimulations lumineuses. Si vous êtes en détresse grave, contactez un professionnel ou les services d'urgence.
              </p>
            </div>
          </motion.main>
        )}

        {view === "session" && (
          <motion.div key="session" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0A0F0D] z-50 flex flex-col items-center justify-center"
            data-testid="emdr-session"
          >
            {/* Moving dot area */}
            <div className="flex-1 w-full flex items-center justify-center relative overflow-hidden">
              {running && (
                <motion.div
                  className="absolute"
                  animate={{ x: ["-38vw", "38vw", "-38vw"] }}
                  transition={{
                    duration: currentSpeed.duration * 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  {/* Outer halo */}
                  <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'radial-gradient(circle, rgba(107,133,118,0.15) 0%, transparent 70%)' }}>
                    {/* Middle halo */}
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'radial-gradient(circle, rgba(107,133,118,0.25) 0%, transparent 70%)' }}>
                      {/* Core dot */}
                      <div className="w-5 h-5 rounded-full" style={{
                        background: 'radial-gradient(circle, #A8C5B5 0%, #6B8576 60%, #4A6B58 100%)',
                        boxShadow: '0 0 20px rgba(107,133,118,0.6), 0 0 40px rgba(107,133,118,0.3), 0 0 60px rgba(107,133,118,0.1)'
                      }} />
                    </div>
                  </div>
                </motion.div>
              )}

              {!running && timeLeft > 0 && timeLeft < duration && (
                <div className="text-center">
                  <p className="text-white/60 text-sm mb-4" style={{ fontFamily: 'Manrope' }}>En pause</p>
                  <div className="w-5 h-5 rounded-full mx-auto" style={{
                    background: 'radial-gradient(circle, #A8C5B5 0%, #6B8576 60%)',
                    boxShadow: '0 0 20px rgba(107,133,118,0.4)'
                  }} />
                </div>
              )}

              {timeLeft === 0 && (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center px-6">
                  <div className="w-16 h-16 rounded-full mx-auto mb-6" style={{
                    background: 'radial-gradient(circle, rgba(107,133,118,0.2) 0%, transparent 70%)'
                  }}>
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-5 h-5 rounded-full" style={{
                        background: 'radial-gradient(circle, #A8C5B5 0%, #6B8576 60%)',
                        boxShadow: '0 0 15px rgba(107,133,118,0.4)'
                      }} />
                    </div>
                  </div>
                  <p className="text-white/80 text-lg mb-2" style={{ fontFamily: 'Outfit' }}>Séance terminée</p>
                  <p className="text-white/40 text-sm mb-8" style={{ fontFamily: 'Manrope' }}>Prenez quelques instants avant de reprendre vos activités.</p>
                  <div className="flex flex-col gap-3 max-w-xs mx-auto">
                    <button onClick={() => { setTimeLeft(duration); startSession(); }} className="py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300" data-testid="emdr-restart">
                      Recommencer
                    </button>
                    <button onClick={() => setView("intro")} className="py-3 text-white/50 hover:text-white/80 transition-colors duration-200 text-sm font-medium" data-testid="emdr-quit-session">
                      Quitter
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Bottom controls */}
            <div className="w-full px-6 pb-8 pt-4">
              <div className="max-w-md mx-auto flex items-center justify-between">
                <button onClick={() => setView("intro")} className="p-3 text-white/30 hover:text-white/60 transition-colors duration-200" data-testid="emdr-close">
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>

                <div className="flex items-center gap-4">
                  {timeLeft > 0 && (
                    <>
                      <span className="text-white/40 text-sm font-mono" data-testid="emdr-timer">{formatTime(timeLeft)}</span>
                      <button onClick={togglePause} className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center transition-colors duration-200" data-testid="emdr-pause">
                        {running ? <Pause className="w-5 h-5 text-white/80" strokeWidth={2} /> : <Play className="w-5 h-5 text-white/80" strokeWidth={2} />}
                      </button>
                    </>
                  )}
                </div>

                <button onClick={() => setShowSettings(!showSettings)} className="p-3 text-white/30 hover:text-white/60 transition-colors duration-200" data-testid="emdr-settings-toggle">
                  <Settings className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>

              {/* Inline settings */}
              <AnimatePresence>
                {showSettings && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                    className="max-w-md mx-auto mt-4 bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10"
                  >
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-white/40 mb-2">Vitesse</p>
                        <div className="flex gap-2">
                          {SPEEDS.map(s => (
                            <button key={s.id} onClick={() => setSpeed(s.id)}
                              className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all duration-200 ${speed === s.id ? 'bg-[#6B8576] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-white/40 mb-2">Durée</p>
                        <div className="flex gap-2">
                          {DURATIONS.map(d => (
                            <button key={d.id} onClick={() => { setDuration(d.id); setTimeLeft(d.id); stopSession(); }}
                              className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all duration-200 ${duration === d.id ? 'bg-[#6B8576] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
